/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IGroup,
    IGroups,
    IHelp,
    IOrderConfirmationViewProps,
    IOrderInformation,
    IOrderSummary,
    IPaymentMethods,
    ISalesLine
} from '@msdyn365-commerce-modules/order-management';
import { SalesOrder } from '@msdyn365-commerce/retail-proxy';
import { ReactElement } from 'react';

interface IPostalAddress {
    Name?: string;
    Street?: string;
    StreetNumber?: string;
    ZipCode?: string;
    City?: string;
    ThreeLetterISORegionName?: string;
}

interface IOrderConfirmationGroupProps extends IGroup {
    moduleProps: any;
}

interface IOrderConfirmationGroupsProps extends IGroups {
    moduleProps: any;
}

interface IAddressElementProps {
    address: IPostalAddress;
}

interface IOrderConfirmationHeaderInformationProps {
    moduleProps: any;
    orderInfomation?: IOrderInformation;
    salesOrder?: SalesOrder;
    locale?: string;
}

interface IOrderConfirmationShipToInformationProps {
    moduleProps: any;
    groups?: IGroups;
}

interface IOrderConfirmationReferenceInformationProps {
    moduleProps: any;
    salesOrder?: SalesOrder;
}

interface IOrderConfirmationSummaryInformationProps {
    moduleProps: any;
    orderSummary?: IOrderSummary;
    salesOrder?: SalesOrder;
}

const getFirstOrderNoteDescription = (salesOrder?: SalesOrder): string | undefined => {
    const notes: any = salesOrder?.Notes;

    if (!notes) {
        return undefined;
    }

    if (Array.isArray(notes)) {
        return notes[0]?.Description || notes[0]?.Text;
    }

    return notes?.values?.().next?.().value?.Description;
};

const getProcessingFeeTotalFromSalesOrder = (salesOrder?: SalesOrder): number => {
    const salesLines = salesOrder?.SalesLines ?? [];

    return salesLines.reduce((total, salesLine: any) => {
        const chargeLines = salesLine.ChargeLines ?? [];

        const processingFeeForLine = chargeLines.reduce((sum: number, chargeLine: { CalculatedAmount?: number }) => {
            return sum + Number(chargeLine.CalculatedAmount ?? 0);
        }, 0);

        return total + processingFeeForLine;
    }, 0);
};

const getSalesOrderChargeAmount = (salesOrder?: SalesOrder): number => {
    return Number((salesOrder as any)?.ChargeAmount ?? 0);
};

const getCurrencyCode = (moduleProps: any): string => {
    const cultureFormatter = moduleProps.context?.cultureFormatter as { currencyCode?: string } | undefined;
    return cultureFormatter?.currencyCode ?? 'CHF';
};

const getLocale = (moduleProps: any): string | undefined => {
    const cultureFormatter = moduleProps.context?.cultureFormatter as { locale?: string } | undefined;

    return cultureFormatter?.locale;
};

const formatOrderConfirmationCurrency = (moduleProps: any, amount: number): string => {
    const locale = getLocale(moduleProps);
    const currency = getCurrencyCode(moduleProps);

    if (locale === 'fr-CH') {
        const cultureFormatter = moduleProps.context?.cultureFormatter as
            | { formatCurrency?: (price: number, currencyCode?: string) => string }
            | undefined;

        return cultureFormatter?.formatCurrency ? cultureFormatter.formatCurrency(amount, currency) : `${amount.toFixed(2)} ${currency}`;
    }

    return `${currency} ${amount.toFixed(2)}`;
};

const getFirstDeliveryAddress = (groups?: IGroups): IPostalAddress | undefined => {
    const firstGroup = groups?.groups?.[0];
    const addressElement = firstGroup?.delivery?.address as ReactElement<IAddressElementProps> | undefined;

    return addressElement?.props?.address;
};

export const CLASS_NAME: string = 'ms-order-confirmation';

const formatDate = (dateValue?: string | Date, locale?: string): string | undefined => {
    if (!dateValue) {
        return undefined;
    }

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
        return undefined;
    }

    return date.toLocaleDateString(locale);
};

const getRequestedDeliveryDate = (salesOrder?: SalesOrder): string | undefined => {
    const attributeValues: any = salesOrder?.AttributeValues;

    if (!attributeValues) {
        return undefined;
    }

    const values = Array.isArray(attributeValues) ? attributeValues : Array.from(attributeValues);

    const requestedDeliveryDateAttribute = values.find((attribute: any) => attribute?.Name === 'REQUESTED_DELIVERY_DATE');

    return requestedDeliveryDateAttribute?.TextValue;
};

export const OrderConfirmationInformation: React.FC<IOrderConfirmationHeaderInformationProps> = ({
    moduleProps,
    orderInfomation,
    salesOrder,
    locale
}) => {
    const createdDate = formatDate(salesOrder?.CreatedDateTime, locale);
    const requestedDeliveryDate = getRequestedDeliveryDate(salesOrder);

    const renderInformation = (label: string, value: React.ReactNode) => (
        <p className='cip-order-details__order-information'>
            <span className='ms-order-details__order-summary-label'>{label}</span>
            <span>{value}</span>
        </p>
    );

    return (
        <div className='cip-order-details__head-information'>
            {createdDate && renderInformation(moduleProps.resources.orderConfirmationViewCreationDateLabel, createdDate)}

            {salesOrder?.SalesId && renderInformation(moduleProps.resources.orderConfirmationViewOrderNumberLabel, salesOrder.SalesId)}

            {requestedDeliveryDate &&
                renderInformation(
                    moduleProps.resources.orderConfirmationViewRequestedDeliveryDateLabel,
                    new Date(requestedDeliveryDate).toLocaleDateString(locale)
                )}
        </div>
    );
};

export const OrderConfirmationShipToInformation: React.FC<IOrderConfirmationShipToInformationProps> = ({ moduleProps, groups }) => {
    const address = getFirstDeliveryAddress(groups);

    return (
        <div className='cip-order-details__head-information'>
            {moduleProps.resources?.shipToLabel && (
                <div className='ms-order-details__order-summary-label'>{moduleProps.resources.shipToLabel}</div>
            )}

            {address && (
                <div>
                    {address.Name && <div>{address.Name}</div>}

                    {(address.Street || address.StreetNumber) && (
                        <div>
                            {address.Street} {address.StreetNumber}
                        </div>
                    )}

                    {(address.ZipCode || address.City) && (
                        <div>
                            {address.ZipCode} {address.City}
                        </div>
                    )}

                    {address.ThreeLetterISORegionName && <div>{address.ThreeLetterISORegionName}</div>}
                </div>
            )}
        </div>
    );
};

export const OrderConfirmationReferenceInformation: React.FC<IOrderConfirmationReferenceInformationProps> = ({
    moduleProps,
    salesOrder
}) => {
    const noteDescription = getFirstOrderNoteDescription(salesOrder);

    const renderInformation = (label: string, value: React.ReactNode) => (
        <p className='cip-order-details__order-information'>
            <span className='ms-order-details__order-summary-label'>{label}</span>
            <span>{value}</span>
        </p>
    );

    const hasComment = Boolean(salesOrder?.Comment);
    const hasCustomerRequisition = Boolean(salesOrder?.CustomerRequisition);
    const hasNote = Boolean(noteDescription);

    const hasAdditionalInformation = hasComment || hasCustomerRequisition || hasNote;

    return (
        <div className='cip-order-details__head-information'>
            {moduleProps.resources?.orderConfirmationViewAdditionalInformationLabel && (
                <p className='ms-order-details__order-summary-heading'>
                    {moduleProps.resources.orderConfirmationViewAdditionalInformationLabel}
                </p>
            )}

            {hasComment && renderInformation(moduleProps.resources.orderConfirmationViewCustomerOrderNumberLabel, salesOrder?.Comment)}

            {hasCustomerRequisition &&
                renderInformation(moduleProps.resources.orderConfirmationViewCustomerRequisitionLabel, salesOrder?.CustomerRequisition)}

            {hasNote && renderInformation(moduleProps.resources.orderConfirmationViewCustomerNoteLabel, noteDescription)}

            {!hasAdditionalInformation && (
                <p className='ms-order-details__info-reference-empty'>
                    {moduleProps.resources.orderConfirmationViewNoAdditionalInformationText}
                </p>
            )}
        </div>
    );
};

export const OrderConfirmationSummaryInformation: React.FC<IOrderConfirmationSummaryInformationProps> = ({
    moduleProps,
    orderSummary,
    salesOrder
}) => {
    const processingFeeTotal = getProcessingFeeTotalFromSalesOrder(salesOrder);
    const shippingTotal = getSalesOrderChargeAmount(salesOrder);
    const surchargesAndDiscountsTotal = shippingTotal - processingFeeTotal;

    const surchargesAndDiscountsLabel = moduleProps.resources?.orderConfirmationSurchargesAndDiscountsLabel;
    const shippingCostsLabel = moduleProps.resources?.orderConfirmationShippingCostsLabel;

    const hasProcessingFee = processingFeeTotal !== 0;
    const hasShippingCosts = surchargesAndDiscountsTotal !== 0;

    return (
        <div className='cip-order-details__head-information'>
            {moduleProps.resources?.orderSummaryTitle && (
                <p className='ms-order-details__order-summary-heading'>{moduleProps.resources.orderSummaryTitle}</p>
            )}

            {orderSummary?.subtotal}

            {hasProcessingFee && (
                <p className='ms-order-confirmation__order-summary-line-subtotal'>
                    <span className='ms-order-confirmation__order-summary-label'>{surchargesAndDiscountsLabel}</span>
                    <span className='msc-price ms-order-confirmation__order-summary-price'>
                        <span className='msc-price__actual'>{formatOrderConfirmationCurrency(moduleProps, processingFeeTotal)}</span>
                    </span>
                </p>
            )}

            <p className='ms-order-confirmation__order-summary-line-subtotal'>
                <span className='ms-order-confirmation__order-summary-label'>{shippingCostsLabel}</span>
                <span className='msc-price ms-order-confirmation__order-summary-price'>
                    <span className='msc-price__actual'>
                        {hasShippingCosts ? formatOrderConfirmationCurrency(moduleProps, surchargesAndDiscountsTotal) : '-'}
                    </span>
                </span>
            </p>

            {orderSummary?.tax}

            {orderSummary?.totalAmount && (
                <div className='cip-order-details__order-information cip-order-details__order-summary-total-amount'>
                    {orderSummary.totalAmount}
                </div>
            )}

            {orderSummary?.earnedPoints}
        </div>
    );
};

export const OrderConfirmationSalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton }) => (
    <Node {...salesLineProps}>{salesLine}</Node>
);

export const OrderConfirmationGroup: React.FC<IOrderConfirmationGroupProps> = ({
    groupProps,
    delivery,
    salesLinesProps,
    salesLines,
    moduleProps
}) => (
    <Node {...groupProps}>
        {delivery && delivery.shippingItemsToYou}

        {salesLines && (
            <Node {...salesLinesProps}>
                <div className='cip-order-details__sales-lines-header'>
                    <span className='cip-order-details__sales-lines-header-product' />
                    <span className='cip-order-details__sales-lines-header-surface'>{moduleProps.resources?.surfaceFinishingLabel}</span>
                    <span className='cip-order-details__sales-lines-header-quantity'>{moduleProps.resources?.quantityAndUnitLabel}</span>
                </div>

                {salesLines.map((salesLine: ISalesLine) => (
                    <OrderConfirmationSalesLine key={salesLine.data.salesLine.LineId} {...salesLine} />
                ))}
            </Node>
        )}
    </Node>
);

export const OrderConfirmationGroups: React.FC<IOrderConfirmationGroupsProps> = ({ groupsProps, groups, moduleProps }) => (
    <Node {...groupsProps}>
        {groups.map((group: IGroup, index: number) => (
            <OrderConfirmationGroup key={index} {...group} moduleProps={moduleProps} />
        ))}
    </Node>
);

export const OrderConfirmationOrderSummary: React.FC<IOrderSummary> = ({
    orderSummaryProps,
    heading,
    subtotal,
    shipping,
    tax,
    totalAmount,
    earnedPoints
}) => {
    return (
        <Node {...orderSummaryProps}>
            {heading}
            {subtotal}
            {tax}

            <div className='mms-order-confirmation__order-summary-line-total-amount'>{totalAmount}</div>
            {earnedPoints}
        </Node>
    );
};

export const OrderConfirmationPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

export const OrderConfirmationHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

const OrderConfirmationView: React.FC<IOrderConfirmationViewProps> = ({
    moduleProps,
    heading,
    backToShoppingLink,
    alert,
    loading,
    orderInfomation,
    orderSummary,
    payment,
    help,
    groups,
    salesOrder
}) => {
    const locale = moduleProps.moduleProps.context?.request?.locale;
    const hasOrderConfirmationData = Boolean(salesOrder || orderInfomation || groups?.groups?.length || orderSummary || payment);

    if (!hasOrderConfirmationData) {
        return (
            <Module {...moduleProps}>
                {heading}
                {alert}
                {loading}
            </Module>
        );
    }

    return (
        <Module {...moduleProps}>
            {heading}
            {alert}
            {loading}

            {orderInfomation?.receiptEmail && <div className='ms-order-confirmation__receipt-email'>{orderInfomation.receiptEmail}</div>}

            <div className='cip-order-details__header'>
                <OrderConfirmationInformation
                    orderInfomation={orderInfomation}
                    salesOrder={salesOrder}
                    moduleProps={moduleProps.moduleProps}
                    locale={locale}
                />

                <OrderConfirmationShipToInformation moduleProps={moduleProps.moduleProps} groups={groups} />

                <OrderConfirmationReferenceInformation moduleProps={moduleProps.moduleProps} salesOrder={salesOrder} />

                <OrderConfirmationSummaryInformation
                    moduleProps={moduleProps.moduleProps}
                    orderSummary={orderSummary}
                    salesOrder={salesOrder}
                />
            </div>

            {backToShoppingLink}

            {groups && <OrderConfirmationGroups {...groups} moduleProps={moduleProps.moduleProps} />}

            {payment && <OrderConfirmationPayment {...payment} />}

            {help && <OrderConfirmationHelp {...help} />}
        </Module>
    );
};

export default OrderConfirmationView;
